import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDropzone } from 'react-dropzone';
import { FiFile, FiUpload, FiDownload, FiTrash2, FiEdit, FiX, FiFolder, FiFileText, FiImage, FiEye, FiChevronLeft, FiChevronRight, FiUserPlus, FiLoader, FiCloud, FiExternalLink } from 'react-icons/fi';
import { AiOutlineFilePdf, AiOutlineFileWord, AiOutlineFileExcel, AiOutlineFileZip } from 'react-icons/ai';
import { BsFiletypeXlsx, BsFiletypeCsv, BsRobot } from 'react-icons/bs';
import { MdEmail, MdAudiotrack } from 'react-icons/md';
import { documentsAPI, foldersAPI, adminAPI, usersAPI, actionsAPI } from '../lib/api';
import useAuthStore from '../store/authStore';
import CsvExtractModal from '../components/CsvExtractModal';
import { useToast } from '../components/ToastContainer';

const Documents = () => {
  const profile = useAuthStore((state) => state.profile);
  const isAdmin = profile?.role === 'admin';
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [editingDocId, setEditingDocId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', tags: '' });
  const [editError, setEditError] = useState('');
  const [folderUploadMode, setFolderUploadMode] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [targetFolderId, setTargetFolderId] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [filterType, setFilterType] = useState('all');
  const [filterAiProcessed, setFilterAiProcessed] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [duplicateDialog, setDuplicateDialog] = useState(null);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [csvExtractDoc, setCsvExtractDoc] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignDoc, setAssignDoc] = useState(null);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [assignForm, setAssignForm] = useState({
    assigned_to: '',
    action_type: 'approval',
    priority: 'normal',
    due_date: '',
    notes: '',
  });
  const queryClient = useQueryClient();
  const { showSuccess, showError, showInfo } = useToast();
  const isAdminOrManager = profile?.role === 'admin' || profile?.role === 'manager';

  // Get items per page setting
  const { data: itemsPerPageData } = useQuery({
    queryKey: ['settings', 'items_per_page'],
    queryFn: () => adminAPI.getPublicSetting('items_per_page'),
  });

  const itemsPerPage = parseInt(itemsPerPageData?.data?.setting_value || '20');

  const { data, isLoading } = useQuery({
    queryKey: ['documents', selectedFolder],
    queryFn: () => documentsAPI.getAll({ folder_id: selectedFolder }),
  });

  const { data: foldersData } = useQuery({
    queryKey: ['folders'],
    queryFn: () => foldersAPI.getAll(),
  });

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersAPI.getAll(),
    enabled: true,
  });

  // Helper function to build clean folder path without GUIDs
  const buildFolderPath = (folder) => {
    const allFolders = foldersData?.data?.folders || [];

    if (!folder.parent_id) {
      return folder.name;
    }

    const pathParts = [];
    let currentFolder = folder;

    while (currentFolder) {
      pathParts.unshift(currentFolder.name);
      currentFolder = allFolders.find(f => f.id === currentFolder.parent_id);
    }

    return pathParts.join(' / ');
  };

  // Filter and sort documents
  const filteredAndSortedDocuments = useMemo(() => {
    if (!data?.data?.documents || !profile?.id) return [];
    
    let documents = [...data.data.documents];
    
    // Filter to only show documents uploaded by the logged-in user
    documents = documents.filter(doc => doc.uploaded_by === profile.id);
    
    // Apply file type filter
    if (filterType !== 'all') {
      documents = documents.filter(doc => {
        const ext = doc.name?.split('.').pop()?.toLowerCase();
        const mime = doc.mime_type?.toLowerCase();
        
        switch (filterType) {
          case 'pdf':
            return mime?.includes('pdf') || ext === 'pdf';
          case 'word':
            return mime?.includes('word') || ['doc', 'docx'].includes(ext);
          case 'excel':
            return mime?.includes('sheet') || mime?.includes('excel') || ['xls', 'xlsx', 'csv'].includes(ext);
          case 'image':
            return mime?.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext);
          case 'email':
            return ['eml', 'msg'].includes(ext) || mime === 'message/rfc822' || mime === 'application/vnd.ms-outlook';
          case 'other':
            return !['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'eml', 'msg'].includes(ext);
          default:
            return true;
        }
      });
    }
    
    // Apply AI processed filter
    if (filterAiProcessed !== 'all') {
      documents = documents.filter(doc => {
        const hasAiData = doc.document_ai_metadata && doc.document_ai_metadata.length > 0;
        return filterAiProcessed === 'processed' ? hasAiData : !hasAiData;
      });
    }
    
    // Apply sorting
    documents.sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'date_asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'name_asc':
          return a.name.localeCompare(b.name);
        case 'name_desc':
          return b.name.localeCompare(a.name);
        case 'size_desc':
          return (b.file_size || 0) - (a.file_size || 0);
        case 'size_asc':
          return (a.file_size || 0) - (b.file_size || 0);
        default:
          return 0;
      }
    });
    
    // Limit to top 20 most recently uploaded documents
    return documents.slice(0, 20);
  }, [data, filterType, filterAiProcessed, sortBy, profile]);

  // Paginated documents
  const paginatedDocuments = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedDocuments.slice(startIndex, endIndex);
  }, [filteredAndSortedDocuments, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedDocuments.length / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, filterAiProcessed, sortBy, selectedFolder]);

  // Debug: Log document data to check AI metadata
  useEffect(() => {
    console.log('Documents data:', data);
    console.log('Filtered and sorted:', filteredAndSortedDocuments);
    if (data?.data?.documents?.[0]) {
      console.log('Document sample:', data.data.documents[0]);
      console.log('AI Metadata:', data.data.documents[0].document_ai_metadata);
    }
  }, [data, filteredAndSortedDocuments]);

  const uploadMutation = useMutation({
    mutationFn: ({ formData, onProgress }) => {
      // Check if it's a bulk upload by looking for multiple files
      const hasMultipleFiles = formData.getAll('files').length > 0;
      return hasMultipleFiles
        ? documentsAPI.bulkUpload(formData, onProgress)
        : documentsAPI.upload(formData, onProgress);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['documents']);
      setFolderUploadMode(false);
      setIsUploading(false);
      setUploadProgress(0);

      // Show success toast
      const isBulk = data.data?.documents?.length > 1;
      if (isBulk) {
        showSuccess(`Successfully uploaded ${data.data.documents.length} documents`);
      } else {
        const docName = data.data?.documents?.[0]?.name || 'document';
        showSuccess(`Successfully uploaded "${docName}"`);
      }
    },
    onError: (error) => {
      setIsUploading(false);
      setUploadProgress(0);
      showError(`Upload failed: ${error.response?.data?.message || error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => documentsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['documents']);
      showSuccess('Document deleted successfully');
    },
    onError: (error) => {
      showError(`Failed to delete document: ${error.response?.data?.message || error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => documentsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['documents']);
      setEditingDocId(null);
      setEditForm({ name: '', description: '', tags: '' });
      setEditError('');
      showSuccess('Document updated successfully');
    },
    onError: (error) => {
      setEditError(error.response?.data?.message || 'Failed to update document');
      showError(`Failed to update document: ${error.response?.data?.message || error.message}`);
    },
  });

  const assignActionMutation = useMutation({
    mutationFn: (data) => actionsAPI.create(data),
    onMutate: () => {
      showInfo('Sending email notification...', 10000);
    },
    onSuccess: () => {
      setShowAssignModal(false);
      setAssignDoc(null);
      setAssignForm({
        assigned_to: '',
        action_type: 'approval',
        priority: 'normal',
        due_date: '',
        notes: '',
      });
      showSuccess('Action assigned and email notification sent!');
    },
    onError: (error) => {
      showError(`Failed to assign action: ${error.response?.data?.message || error.message}`);
    },
  });

  const bulkAssignActionMutation = useMutation({
    mutationFn: async (data) => {
      // Create actions for all selected documents
      return Promise.all(
        selectedDocIds.map((docId) =>
          actionsAPI.create({
            document_id: docId,
            assigned_to: data.assigned_to,
            action_type: data.action_type,
            priority: data.priority,
            due_date: data.due_date || null,
            notes: data.notes || null,
          })
        )
      );
    },
    onMutate: () => {
      showInfo(`Sending ${selectedDocIds.length} email notification(s)...`, 15000);
    },
    onSuccess: () => {
      setShowBulkAssignModal(false);
      setSelectedDocIds([]);
      setAssignForm({
        assigned_to: '',
        action_type: 'approval',
        priority: 'normal',
        due_date: '',
        notes: '',
      });
      showSuccess('Actions assigned and email notifications sent!');
    },
    onError: (error) => {
      showError(`Failed to assign actions: ${error.response?.data?.message || error.message}`);
    },
  });

  const handleEditClick = (doc) => {
    setEditingDocId(doc.id);
    setEditForm({
      name: doc.name,
      description: doc.description || '',
      tags: doc.tags?.join(', ') || '',
    });
    setEditError('');
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) {
      setEditError('Document name is required');
      return;
    }

    updateMutation.mutate({
      id: editingDocId,
      data: {
        name: editForm.name,
        description: editForm.description || null,
        tags: editForm.tags
          ? editForm.tags.split(',').map(t => t.trim()).filter(t => t)
          : [],
      },
    });
  };

  const handleToggleSelectDoc = (docId) => {
    setSelectedDocIds((prev) =>
      prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDocIds.length === filteredAndSortedDocuments.length) {
      setSelectedDocIds([]);
    } else {
      setSelectedDocIds(filteredAndSortedDocuments.map((doc) => doc.id) || []);
    }
  };

  // Helper function to get file type icon
  const getFileIcon = (filename, mimeType) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    const iconClass = "w-6 h-6";

    if (mimeType?.includes('pdf') || ext === 'pdf') {
      return <AiOutlineFilePdf className={`${iconClass} text-red-500`} />;
    } else if (mimeType?.includes('word') || ['doc', 'docx'].includes(ext)) {
      return <AiOutlineFileWord className={`${iconClass} text-blue-500`} />;
    } else if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet') || ['xls', 'xlsx'].includes(ext)) {
      return <AiOutlineFileExcel className={`${iconClass} text-green-600`} />;
    } else if (ext === 'csv') {
      return <BsFiletypeCsv className={`${iconClass} text-green-500`} />;
    } else if (mimeType?.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(ext)) {
      return <FiImage className={`${iconClass} text-purple-500`} />;
    } else if (mimeType?.includes('audio') || ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac', 'webm', 'opus'].includes(ext)) {
      return <MdAudiotrack className={`${iconClass} text-indigo-500`} />;
    } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
      return <AiOutlineFileZip className={`${iconClass} text-yellow-600`} />;
    } else if (['txt', 'log', 'md'].includes(ext)) {
      return <FiFileText className={`${iconClass} text-gray-500`} />;
    } else if (['eml', 'msg'].includes(ext) || mimeType === 'message/rfc822' || mimeType === 'application/vnd.ms-outlook') {
      return <MdEmail className={`${iconClass} text-orange-500`} />;
    }
    return <FiFile className={`${iconClass} text-gray-400`} />;
  };

  const handleMoveSelected = () => {
    if (selectedDocIds.length === 0) {
      showWarning('Please select at least one document');
      return;
    }
    setShowMoveModal(true);
    setTargetFolderId('');
  };

  const handleMoveSubmit = async (e) => {
    e.preventDefault();

    // Move all selected documents
    try {
      await Promise.all(
        selectedDocIds.map((docId) =>
          documentsAPI.update(docId, {
            folder_id: targetFolderId || null,
          })
        )
      );

      queryClient.invalidateQueries(['documents']);
      setShowMoveModal(false);
      setSelectedDocIds([]);
      setTargetFolderId('');
      showSuccess(`Successfully moved ${selectedDocIds.length} document(s)`);
    } catch (error) {
      showError(`Failed to move documents: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleBulkDelete = () => {
    if (selectedDocIds.length === 0) {
      showWarning('Please select at least one document');
      return;
    }
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await Promise.all(
        selectedDocIds.map((docId) => documentsAPI.delete(docId))
      );

      queryClient.invalidateQueries(['documents']);
      setShowDeleteModal(false);
      setSelectedDocIds([]);
      showSuccess(`Successfully deleted ${selectedDocIds.length} document(s)`);
    } catch (error) {
      showError(`Failed to delete documents: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleAssignClick = (doc) => {
    setAssignDoc(doc);
    setShowAssignModal(true);
    setAssignForm({
      assigned_to: '',
      action_type: 'approval',
      priority: 'normal',
      due_date: '',
      notes: '',
    });
  };

  const handleAssignSubmit = (e) => {
    e.preventDefault();
    if (!assignForm.assigned_to) {
      showError('Please select a user to assign to');
      return;
    }

    assignActionMutation.mutate({
      document_id: assignDoc.id,
      assigned_to: assignForm.assigned_to,
      action_type: assignForm.action_type,
      priority: assignForm.priority,
      due_date: assignForm.due_date || null,
      notes: assignForm.notes || null,
    });
  };

  const handleBulkAssignClick = () => {
    if (selectedDocIds.length === 0) {
      showError('Please select at least one document');
      return;
    }
    setShowBulkAssignModal(true);
    setAssignForm({
      assigned_to: '',
      action_type: 'approval',
      priority: 'normal',
      due_date: '',
      notes: '',
    });
  };

  const handleBulkAssignSubmit = (e) => {
    e.preventDefault();
    if (!assignForm.assigned_to) {
      showError('Please select a user to assign to');
      return;
    }

    bulkAssignActionMutation.mutate(assignForm);
  };

  const checkForDuplicates = async (files, baseFolderPath) => {
    const fileInfos = files.map(file => ({
      name: file.name,
      path: file.webkitRelativePath || file.path || file.name
    }));

    const response = await documentsAPI.checkDuplicates({
      files: fileInfos,
      base_folder_path: baseFolderPath
    });

    return response.data;
  };

  const performUpload = async (files, conflictResolution = 'version') => {
    setIsUploading(true);
    setUploadProgress(0);

    const onProgress = (progressEvent) => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      setUploadProgress(percentCompleted);
    };

    if (folderUploadMode && files.length > 0) {
      // Bulk upload mode for folder structures
      const formData = new FormData();

      files.forEach((file, index) => {
        formData.append('files', file);
        const relativePath = file.webkitRelativePath || file.path || file.name;
        formData.append(`path_${index}`, relativePath);
      });

      if (selectedFolder) {
        formData.append('folder_id', selectedFolder);
      }

      // Extract base folder name from first file's path
      if (files[0].webkitRelativePath) {
        const baseFolderName = files[0].webkitRelativePath.split('/')[0];
        formData.append('base_folder_path', baseFolderName);
      }

      formData.append('conflict_resolution', conflictResolution);

      await uploadMutation.mutateAsync({ formData, onProgress });
    } else {
      // Single file upload mode
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        if (selectedFolder) {
          formData.append('folder_id', selectedFolder);
        }
        formData.append('conflict_resolution', conflictResolution);
        await uploadMutation.mutateAsync({ formData, onProgress });
      }
    }
  };

  const onDrop = async (acceptedFiles) => {
    if (folderUploadMode && acceptedFiles.length > 0) {
      // Check for duplicates first
      const baseFolderName = acceptedFiles[0].webkitRelativePath?.split('/')[0];

      try {
        const duplicates = await checkForDuplicates(acceptedFiles, baseFolderName);

        if (duplicates.folders.length > 0 || duplicates.documents.length > 0) {
          // Show duplicate dialog
          setDuplicateDialog({
            folders: duplicates.folders,
            documents: duplicates.documents
          });
          setPendingUpload(acceptedFiles);
          return;
        }

        // No duplicates found - proceed with folder upload
        console.log('No duplicates found in folder upload, proceeding with upload');
        await performUpload(acceptedFiles);
        return;
      } catch (error) {
        console.error('Error checking duplicates:', error);
        // On error, proceed with upload anyway
        await performUpload(acceptedFiles);
        return;
      }
    } else if (!folderUploadMode && acceptedFiles.length > 0) {
      // Single file upload - check for duplicates
      console.log('Checking for duplicates in folder:', selectedFolder);
      try {
        const fileInfos = acceptedFiles.map(file => ({
          name: file.name,
          path: file.name
        }));
        
        const duplicates = await documentsAPI.checkDuplicates({
          files: fileInfos,
          base_folder_path: null,
          folder_id: selectedFolder
        });
        
        console.log('Duplicate check response:', duplicates.data);
        
        if (duplicates.data.documents.length > 0) {
          // Show duplicate dialog
          console.log('Duplicates found, showing dialog');
          setDuplicateDialog({
            folders: [],
            documents: duplicates.data.documents
          });
          setPendingUpload(acceptedFiles);
          return; // Don't proceed with upload
        }
        
        console.log('No duplicates found, proceeding with upload');
        // No duplicates found - proceed with upload
        await performUpload(acceptedFiles);
      } catch (error) {
        console.error('Error checking duplicates:', error);
        // On error, proceed with upload anyway
        await performUpload(acceptedFiles);
      }
      return; // Exit after handling single file upload
    }

    // This shouldn't be reached, but just in case
    console.warn('onDrop: No valid upload mode detected');
  };

  // Handle folder selection via native input for better webkitRelativePath support
  const handleFolderInputChange = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      await onDrop(files);
    }
    // Reset the input so the same folder can be selected again
    event.target.value = '';
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    noClick: false,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      'text/plain': ['.txt'],
      'text/csv': ['.csv'],
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.bmp'],
      'message/rfc822': ['.eml'],
      'application/vnd.ms-outlook': ['.msg'],
      'audio/*': ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.webm', '.opus'],
    },
  });

  const handleDownload = async (doc) => {
    try {
      const response = await documentsAPI.download(doc.id);

      // response.data should be a Blob when using responseType: 'blob'
      const blob = response.data;

      // Verify we got a blob
      if (!(blob instanceof Blob)) {
        console.error('Expected Blob, got:', typeof blob);
        throw new Error('Invalid response format - expected binary data');
      }

      // Verify blob has content
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      console.log('Downloaded blob:', {
        size: blob.size,
        type: blob.type,
        expectedSize: doc.size
      });

      // Extract filename from Content-Disposition header or use document name
      let filename = doc.name || 'download';
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = decodeURIComponent(filenameMatch[1]);
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      // Clean up
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Download error:', error);
      showError(`Failed to download document: ${error.response?.data?.message || error.message}`);
    }
  };

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Documents</h1>
          <p className="text-gray-600 mt-2">Manage your documents</p>
        </div>
        <button
          onClick={() => setFolderUploadMode(!folderUploadMode)}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            folderUploadMode
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {folderUploadMode ? 'Folder Upload Mode' : 'Single File Mode'}
        </button>
      </div>

      {/* Upload Zone */}
      <div className="relative">
        {folderUploadMode ? (
          // Native file input for folder uploads to preserve webkitRelativePath
          <div className="card mb-6 border-2 border-dashed border-gray-300">
            <input
              type="file"
              webkitdirectory=""
              directory=""
              multiple
              onChange={handleFolderInputChange}
              className="hidden"
              id="folder-upload-input"
            />
            <label
              htmlFor="folder-upload-input"
              className="cursor-pointer block text-center py-8"
            >
              <FiUpload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-900">
                Click to select a folder
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Upload an entire folder with its directory structure
              </p>
            </label>
          </div>
        ) : (
          // React-dropzone for single file uploads
          <div>
            {/* Folder Selector for Single File Upload */}
            <div className="mb-4 flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700">Upload to folder:</label>
              <select
                value={selectedFolder || ''}
                onChange={(e) => setSelectedFolder(e.target.value || null)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Root (No folder)</option>
                {foldersData?.data?.folders?.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {buildFolderPath(folder)}
                  </option>
                ))}
              </select>
            </div>
            
            <div
              {...getRootProps()}
              className={`card border-2 border-dashed cursor-pointer transition-colors ${
                isDragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300'
              }`}
            >
            <input
              {...getInputProps({
                multiple: true,
              })}
            />
            <div className="text-center py-8">
              <FiUpload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-900">
                {isDragActive
                  ? 'Drop files here'
                  : 'Drag & drop files or click to upload'}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV, PNG, JPG, EML, MSG (max 50MB)
              </p>
            </div>
          </div>
          </div>
        )}
      </div>

      {/* Upload Progress Bar */}
      {isUploading && (
        <div className="card mb-6">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {folderUploadMode ? 'Uploading folder...' : 'Uploading document...'}
            </span>
            <span className="text-sm font-medium text-primary-600">
              {uploadProgress}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-primary-600 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Documents List */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            Your latest uploaded documents
          </h2>
          {selectedDocIds.length > 0 && (
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-600">
                {selectedDocIds.length} selected
              </span>
              <button
                onClick={handleBulkAssignClick}
                className="btn-primary flex items-center space-x-2"
              >
                <FiUserPlus className="w-4 h-4" />
                <span>Assign Action</span>
              </button>
              <button
                onClick={handleMoveSelected}
                className="btn-primary flex items-center space-x-2"
              >
                <FiFolder className="w-4 h-4" />
                <span>Move to Folder</span>
              </button>
              {isAdmin && (
                <button
                  onClick={handleBulkDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
                >
                  <FiTrash2 className="w-4 h-4" />
                  <span>Delete Selected</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Filters and Sorting */}
        {data?.data?.documents?.length > 0 && (
          <div className="flex flex-wrap items-center gap-4 mb-4 pb-4 border-b border-gray-200">
            {/* Sort By */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="date_desc">Newest First</option>
                <option value="date_asc">Oldest First</option>
                <option value="name_asc">Name (A-Z)</option>
                <option value="name_desc">Name (Z-A)</option>
                <option value="size_desc">Largest First</option>
                <option value="size_asc">Smallest First</option>
              </select>
            </div>

            {/* File Type Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">File type:</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">All Types</option>
                <option value="pdf">PDF</option>
                <option value="word">Word</option>
                <option value="excel">Excel/CSV</option>
                <option value="image">Images</option>
                <option value="email">Email (EML/MSG)</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* AI Processed Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">AI Status:</label>
              <select
                value={filterAiProcessed}
                onChange={(e) => setFilterAiProcessed(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">All Documents</option>
                <option value="processed">AI Processed</option>
                <option value="unprocessed">Not Processed</option>
              </select>
            </div>

            {/* Active Filters Indicator */}
            {(filterType !== 'all' || filterAiProcessed !== 'all' || sortBy !== 'date_desc') && (
              <button
                onClick={() => {
                  setFilterType('all');
                  setFilterAiProcessed('all');
                  setSortBy('date_desc');
                }}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium ml-auto"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}

        {isLoading ? (
          <p className="text-gray-600">Loading...</p>
        ) : (
          <>
            {filteredAndSortedDocuments.length > 0 && (
              <div className="mb-3 pb-3 border-b border-gray-200">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedDocIds.length === filteredAndSortedDocuments.length}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Select All</span>
                </label>
              </div>
            )}
            <div className="space-y-2">
              {paginatedDocuments.length > 0 ? (
                paginatedDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors border border-gray-200"
                >
                  <div className="flex items-center space-x-4 flex-1 min-w-0">
                    <input
                      type="checkbox"
                      checked={selectedDocIds.includes(doc.id)}
                      onChange={() => handleToggleSelectDoc(doc.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    {/* File Type Icon */}
                    {getFileIcon(doc.name, doc.mime_type)}
                    
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">{doc.name}</p>
                      {/* AI Processed Icon */}
                      {doc.document_ai_metadata && doc.document_ai_metadata.length > 0 && (
                        <span
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-purple-100 text-purple-700"
                          title="AI Processed"
                        >
                          <BsRobot className="w-3 h-3" />
                          AI
                        </span>
                      )}
                      {/* SharePoint Source Badge */}
                      {doc.source === 'sharepoint' && (
                        <span
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-blue-100 text-blue-700"
                          title={doc.external_url ? `SharePoint: ${doc.external_url}` : 'SharePoint Document'}
                        >
                          <FiCloud className="w-3 h-3" />
                          SharePoint
                          {doc.external_url && (
                            <a
                              href={doc.external_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center hover:text-blue-900"
                              title="Open in SharePoint"
                            >
                              <FiExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-1">
                      <span className="text-sm text-gray-500">
                        {(doc.file_size / 1024).toFixed(2)} KB
                      </span>
                      <span className="text-sm text-gray-500">
                        {new Date(doc.created_at).toLocaleDateString()}
                      </span>
                      
                      {/* Version Badge */}
                      {doc.version && doc.version > 1 && (
                        <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded font-medium">
                          v{doc.version}
                        </span>
                      )}
                      
                      {/* Document Type */}
                      {doc.document_ai_metadata?.[0]?.document_type && (
                        <span className="text-xs px-2 py-1 bg-primary-100 text-primary-700 rounded">
                          {doc.document_ai_metadata[0].document_type}
                        </span>
                      )}
                      
                      {/* Folder Tag */}
                      {doc.folder && (
                        <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 flex items-center gap-1">
                          <FiFolder className="w-3 h-3" />
                          {doc.folder.name}
                        </span>
                      )}
                      
                      {/* AI Discovered Tags */}
                      {doc.document_ai_metadata?.[0]?.suggested_tags && doc.document_ai_metadata[0].suggested_tags.length > 0 && (
                        <>
                          {doc.document_ai_metadata[0].suggested_tags.slice(0, 3).map((tag, idx) => (
                            <span key={`ai-${idx}`} className="text-xs px-2 py-1 rounded bg-purple-50 text-purple-700 border border-purple-200">
                              {tag}
                            </span>
                          ))}
                        </>
                      )}
                      
                      {/* User Tags */}
                      {doc.tags && doc.tags.length > 0 && (
                        <div className="flex items-center gap-1 flex-wrap">
                          {doc.tags.slice(0, 3).map((tag, idx) => (
                            <span key={idx} className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">
                              {tag}
                            </span>
                          ))}
                          {doc.tags.length > 3 && (
                            <span className="text-xs text-gray-500">
                              +{doc.tags.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {/* View Button */}
                  <button
                    onClick={() => window.location.href = `/documents/${doc.id}`}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="View Details"
                  >
                    <FiEye className="w-5 h-5" />
                  </button>
                  {/* Edit Button */}
                  <button
                    onClick={() => handleEditClick(doc)}
                    className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <FiEdit className="w-5 h-5" />
                  </button>
                  {/* Download Button */}
                  <button
                    onClick={() => handleDownload(doc)}
                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Download"
                  >
                    <FiDownload className="w-5 h-5" />
                  </button>
                  {/* CSV Extract Button */}
                  <button
                    onClick={() => setCsvExtractDoc(doc)}
                    className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    title="CSV Extract - AI powered data extraction"
                  >
                    <BsFiletypeCsv className="w-5 h-5" />
                  </button>
                  {/* Assign Action Button */}
                  <button
                    onClick={() => handleAssignClick(doc)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Assign Action"
                  >
                    <FiUserPlus className="w-5 h-5" />
                  </button>
                  {/* Delete Button - Admin Only */}
                  {isAdmin && (
                    <button
                      onClick={() => deleteMutation.mutate(doc.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete (Admin Only)"
                    >
                      <FiTrash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            ))
              ) : (
                <p className="text-gray-500 text-center py-8">
                  No documents match your filters. Try adjusting the filters above.
                </p>
              )}
            </div>

            {/* Pagination Controls */}
            {filteredAndSortedDocuments.length > itemsPerPage && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedDocuments.length)} of {filteredAndSortedDocuments.length} documents
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Previous page"
                  >
                    <FiChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded-lg transition-colors ${
                          currentPage === page
                            ? 'bg-primary-600 text-white'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Next page"
                  >
                    <FiChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Move Document Modal */}
      {showMoveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">
                Move {selectedDocIds.length} Document{selectedDocIds.length > 1 ? 's' : ''}
              </h3>
              <button
                onClick={() => {
                  setShowMoveModal(false);
                  setTargetFolderId('');
                }}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleMoveSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Target Folder
                </label>
                <select
                  value={targetFolderId}
                  onChange={(e) => setTargetFolderId(e.target.value)}
                  className="input"
                >
                  <option value="">Root (No folder)</option>
                  {foldersData?.data?.folders?.map((folder) => (
                    <option key={folder.id} value={folder.id}>
                      {folder.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-3 pt-4">
                <button
                  type="submit"
                  className="btn-primary flex-1"
                >
                  Move {selectedDocIds.length} Document{selectedDocIds.length > 1 ? 's' : ''}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowMoveModal(false);
                    setTargetFolderId('');
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">
                Confirm Delete
              </h3>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <FiTrash2 className="w-6 h-6 text-red-600" />
                  </div>
                </div>
                <div>
                  <p className="text-gray-900 font-medium">
                    Delete {selectedDocIds.length} document{selectedDocIds.length > 1 ? 's' : ''}?
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    This action cannot be undone. The selected document{selectedDocIds.length > 1 ? 's' : ''} will be permanently deleted.
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3 pt-4">
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex-1 font-medium"
                >
                  Delete {selectedDocIds.length} Document{selectedDocIds.length > 1 ? 's' : ''}
                </button>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Document Modal */}
      {editingDocId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Edit Document</h3>
              <button
                onClick={() => {
                  setEditingDocId(null);
                  setEditError('');
                }}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
              {editError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                  {editError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="input"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="input"
                  rows="3"
                  placeholder="Optional description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
                <input
                  type="text"
                  value={editForm.tags}
                  onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                  className="input"
                  placeholder="e.g., invoice, finance, 2024"
                />
              </div>

              <div className="flex items-center space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="btn-primary flex-1"
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingDocId(null)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Duplicate Detection Dialog */}
      {duplicateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Duplicates Detected</h3>
              <button
                onClick={() => {
                  setDuplicateDialog(null);
                  setPendingUpload(null);
                }}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <p className="text-gray-600 text-base">
                The following items already exist in your document library. Choose how to handle them:
              </p>

              <div className="space-y-4">
                {duplicateDialog.folders.length > 0 && (
                  <div className="bg-blue-50 border-l-4 border-blue-400 rounded-r-lg p-4 shadow-sm">
                    <div className="flex items-start">
                      <FiFolder className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-blue-900 mb-2 text-base">
                          {duplicateDialog.folders.length} Existing Folder{duplicateDialog.folders.length > 1 ? 's' : ''}
                        </h4>
                        <div className="space-y-1">
                          {duplicateDialog.folders.slice(0, 3).map((folder, idx) => (
                            <div key={idx} className="text-sm text-blue-800 flex items-center">
                              <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2"></span>
                              {folder.name}
                            </div>
                          ))}
                          {duplicateDialog.folders.length > 3 && (
                            <div className="text-xs text-blue-600 italic ml-3.5">
                              +{duplicateDialog.folders.length - 3} more
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {duplicateDialog.documents.length > 0 && (
                  <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-4 shadow-sm">
                    <div className="flex items-start">
                      <FiFile className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-amber-900 mb-2 text-base">
                          {duplicateDialog.documents.length} Existing Document{duplicateDialog.documents.length > 1 ? 's' : ''}
                        </h4>
                        <div className="space-y-1">
                          {duplicateDialog.documents.slice(0, 3).map((doc, idx) => (
                            <div key={idx} className="text-sm text-amber-800 flex items-center justify-between">
                              <div className="flex items-center">
                                <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mr-2"></span>
                                {doc.name}
                              </div>
                              <span className="text-xs bg-amber-200 px-2 py-0.5 rounded ml-2">v{doc.version}</span>
                            </div>
                          ))}
                          {duplicateDialog.documents.length > 3 && (
                            <div className="text-xs text-amber-600 italic ml-3.5">
                              +{duplicateDialog.documents.length - 3} more
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h5 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Choose Action</h5>
                <div className="space-y-3">
                  <button
                    onClick={async () => {
                      setDuplicateDialog(null);
                      await performUpload(pendingUpload, 'skip');
                      setPendingUpload(null);
                    }}
                    className="w-full bg-white hover:bg-gray-50 border-2 border-gray-300 hover:border-gray-400 text-gray-900 rounded-lg px-5 py-4 transition-all duration-150 text-left group"
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-gray-200 transition-colors">
                        <FiX className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-base mb-1">Skip Duplicates</div>
                        <div className="text-sm text-gray-600">Only upload new files, ignore existing ones</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={async () => {
                      setDuplicateDialog(null);
                      await performUpload(pendingUpload, 'replace');
                      setPendingUpload(null);
                    }}
                    className="w-full bg-white hover:bg-orange-50 border-2 border-orange-300 hover:border-orange-400 text-gray-900 rounded-lg px-5 py-4 transition-all duration-150 text-left group"
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-orange-200 transition-colors">
                        <FiEdit className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-base mb-1">Replace Existing</div>
                        <div className="text-sm text-gray-600">Overwrite current files with new uploads (cannot be undone)</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={async () => {
                      setDuplicateDialog(null);
                      await performUpload(pendingUpload, 'version');
                      setPendingUpload(null);
                    }}
                    className="w-full bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-lg px-5 py-4 transition-all duration-150 text-left group shadow-md"
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-4 group-hover:bg-opacity-30 transition-all">
                        <FiUpload className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-base mb-1">Create New Version (Recommended)</div>
                        <div className="text-sm text-indigo-100">Keep existing files and create version history</div>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV Extract Modal */}
      {csvExtractDoc && (
        <CsvExtractModal
          document={csvExtractDoc}
          onClose={() => setCsvExtractDoc(null)}
        />
      )}

      {/* Assign Action Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Assign Action</h2>
              <p className="text-sm text-gray-600 mb-4">
                Document: <span className="font-medium">{assignDoc?.name}</span>
              </p>
              <form onSubmit={handleAssignSubmit}>
                <div className="space-y-4">
                  {/* Assign To */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assign To <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={assignForm.assigned_to}
                      onChange={(e) => setAssignForm({ ...assignForm, assigned_to: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select a user...</option>
                      {usersData?.data?.users?.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.full_name} ({user.email})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Action Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Action Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={assignForm.action_type}
                      onChange={(e) => setAssignForm({ ...assignForm, action_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="approval">Approval</option>
                      <option value="review">Review</option>
                      <option value="signature">Signature</option>
                      <option value="acknowledgment">Acknowledgment</option>
                      <option value="feedback">Feedback</option>
                    </select>
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={assignForm.priority}
                      onChange={(e) => setAssignForm({ ...assignForm, priority: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>

                  {/* Due Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Due Date
                    </label>
                    <input
                      type="date"
                      value={assignForm.due_date}
                      onChange={(e) => setAssignForm({ ...assignForm, due_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <textarea
                      value={assignForm.notes}
                      onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Add any notes or instructions..."
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAssignModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={assignActionMutation.isPending}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {assignActionMutation.isPending ? (
                      <>
                        <FiLoader className="animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Assign Action'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Assign Modal */}
      {showBulkAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Assign Action to Multiple Documents</h2>
              <p className="text-sm text-gray-600 mb-4">
                Assigning action to <span className="font-medium">{selectedDocIds.length} document(s)</span>
              </p>
              <form onSubmit={handleBulkAssignSubmit}>
                <div className="space-y-4">
                  {/* Assign To */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assign To <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={assignForm.assigned_to}
                      onChange={(e) => setAssignForm({ ...assignForm, assigned_to: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select a user...</option>
                      {usersData?.data?.users?.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.full_name} ({user.email})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Action Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Action Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={assignForm.action_type}
                      onChange={(e) => setAssignForm({ ...assignForm, action_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="approval">Approval</option>
                      <option value="review">Review</option>
                      <option value="signature">Signature</option>
                      <option value="acknowledgment">Acknowledgment</option>
                      <option value="feedback">Feedback</option>
                    </select>
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={assignForm.priority}
                      onChange={(e) => setAssignForm({ ...assignForm, priority: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>

                  {/* Due Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Due Date
                    </label>
                    <input
                      type="date"
                      value={assignForm.due_date}
                      onChange={(e) => setAssignForm({ ...assignForm, due_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <textarea
                      value={assignForm.notes}
                      onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Add any notes or instructions..."
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowBulkAssignModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={bulkAssignActionMutation.isPending}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {bulkAssignActionMutation.isPending ? (
                      <>
                        <FiLoader className="animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Assign Actions'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;
